import type { ReactNode } from 'react';
import { Building2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Typography } from '@/components/ui/typography';
import { hasMultipleOrgs, orgSwitchLabel } from '../../../services/org-data';
import { backLabelFor, type PropertyEntry } from './propertyEntry';

interface Props {
  currentOrgName: string;
  onBackToProperties: () => void;
  /** Omit on surfaces that never switch scope (the internal global-orders
   *  entry, DV-2849) — the Switch Organization button is hidden rather than
   *  rendered with no handler. */
  onSwitchOrg?: () => void;
  /** Which surface is rendering this — drives the label AND the sentence. */
  entry?: PropertyEntry;
}

/**
 * Three surfaces, three sentences. The internal console has no org to name and
 * no switch to offer, and "doesn't exist" would be a flat lie there: until
 * property-service PR #466 deploys its unscoped by-id fast path, this hero is
 * the EXPECTED state for client properties that do exist (DV-2849 §8).
 */
const notFoundMessage = (
  entry: PropertyEntry,
  propertyMayBeElsewhere: boolean,
  currentOrgName: string,
): ReactNode => {
  if (entry === 'global-orders') {
    return (
      <>
        This property isn&apos;t available from the internal console. It may have been
        removed, or it may belong to an account this view can&apos;t reach.
      </>
    );
  }
  if (propertyMayBeElsewhere) {
    return (
      <>
        This property isn&apos;t in <span className="font-medium text-foreground">{currentOrgName}</span>.
        It may belong to a different organization, or it may have been removed.
      </>
    );
  }
  return <>This property doesn&apos;t exist or may have been removed.</>;
};

export function PropertyNotFound({
  currentOrgName, onBackToProperties, onSwitchOrg, entry = 'account',
}: Props) {
  // Two questions, kept apart. Whether the property could be in ANOTHER of the
  // user's orgs is a fact about the user (single-org users, typically
  // CUSTOMER_USER, get the tightened copy) and picks the sentence. Whether this
  // caller can offer the switch only decides the button — a missing handler
  // must not tell a multi-org user the property doesn't exist.
  const propertyMayBeElsewhere = entry === 'account' && hasMultipleOrgs();
  const switchOrg = propertyMayBeElsewhere ? onSwitchOrg : undefined;

  return (
    <div className="flex h-full items-center justify-center p-6">
      <div className="max-w-md text-center space-y-4">
        <Building2 className="h-12 w-12 mx-auto text-muted-foreground" aria-hidden="true" />
        <Typography as="h1" variant="heading-lg" className="font-semibold">
          Property Not Found
        </Typography>
        <Typography className="text-muted-foreground">
          {notFoundMessage(entry, propertyMayBeElsewhere, currentOrgName)}
        </Typography>
        <div className="flex items-center justify-center gap-2 pt-2">
          <Button
            variant={switchOrg ? 'outline' : 'default'}
            onClick={onBackToProperties}
          >
            {backLabelFor(entry)}
          </Button>
          {switchOrg && (
            <Button variant="default" onClick={switchOrg}>
              {orgSwitchLabel('Switch Organization')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
